/**
 * Tracking helpers — Prompt 8
 * --------------------------------------------------------------------------
 * On the very first visit, captures the marketing context (UTMs, gclid,
 * fbclid, ttclid, ref, referrer, landingPage) and persists it for the whole
 * session so we can stamp it on every WhatsApp click — even after language
 * switches and several minutes of navigation.
 *
 *   First-touch attribution lives in `localStorage.slr_tracking_first`.
 *   Last-touch (the most recent UTM-bearing entry) lives in
 *   `localStorage.slr_tracking_last`.
 *
 * Channel detection rules — see Prompt 8 §8.
 */
import { api } from "@/lib/api";

const FIRST_KEY = "slr_tracking_first";
const LAST_KEY = "slr_tracking_last";
const SESSION_KEY = "slr_session_id";

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function readJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / private mode */
  }
}

export function getSessionId() {
  let sid = null;
  try {
    sid = sessionStorage.getItem(SESSION_KEY);
  } catch {
    sid = null;
  }
  if (!sid) {
    sid = uuid();
    try {
      sessionStorage.setItem(SESSION_KEY, sid);
    } catch {
      /* ignore */
    }
  }
  return sid;
}

function parseQuery(search) {
  const out = {};
  const sp = new URLSearchParams(search || "");
  for (const [k, v] of sp.entries()) out[k] = v;
  return out;
}

/** Derive the collaborator display name from marketing context.
 *  Prompt 14 — when a visitor lands via a collaborator referral link
 *  (utm_source=collaborator[&utm_campaign=geoff][&ref=geoff]) we surface
 *  the collaborator name so it flows into every WhatsApp click and shows
 *  up in the admin tracking panel. */
export function detectCollaborator(ctx) {
  const src = (ctx?.source || "").toLowerCase();
  const med = (ctx?.medium || "").toLowerCase();
  if (src !== "collaborator" && med !== "referral") return "";
  const raw = (ctx?.campaign || ctx?.ref || "").trim();
  if (!raw) return "";
  // Title-case the slug: "geoff" → "Geoff", "john-doe" → "John Doe"
  return raw
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(" ");
}

/** Pure helper, exported for unit tests. */
export function detectChannel(ctx) {
  const src = (ctx.source || "").toLowerCase();
  const med = (ctx.medium || "").toLowerCase();
  const ref = (ctx.referrer || "").toLowerCase();

  // Prompt 14 — collaborator referral links, e.g. Geoff:
  //   ?utm_source=collaborator&utm_medium=referral&utm_campaign=geoff&ref=geoff
  if (src === "collaborator" || med === "referral") return "Collaborator";

  if (ctx.gclid) return "Google Ads";
  if (src === "google" && med === "cpc") return "Google Ads";

  if (src === "instagram") return "Instagram";
  if (src === "facebook" || ctx.fbclid) return "Facebook";
  if (src === "tiktok" || ctx.ttclid) return "TikTok";
  if (src === "youtube") return "YouTube";

  if (ref.includes("instagram.")) return "Instagram";
  if (ref.includes("facebook.") || ref.includes("fb.com")) return "Facebook";
  if (ref.includes("tiktok.")) return "TikTok";
  if (ref.includes("youtube.")) return "YouTube";

  if (ref.includes("google.") && !ctx.gclid) return "Google Organic";

  if (!src && !med && !ctx.gclid && !ctx.fbclid && !ctx.ttclid && !ref) return "Direct";
  return "Website Normal";
}

function deviceType() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return "mobile";
  if (/Tablet|iPad/i.test(ua)) return "tablet";
  return "desktop";
}

/** Build the current marketing context from URL + referrer. */
function readIncoming() {
  if (typeof window === "undefined") return null;
  const q = parseQuery(window.location.search);
  const ctx = {
    source: q.utm_source || "",
    medium: q.utm_medium || "",
    campaign: q.utm_campaign || "",
    content: q.utm_content || "",
    term: q.utm_term || "",
    ref: q.ref || "",
    gclid: q.gclid || "",
    fbclid: q.fbclid || "",
    ttclid: q.ttclid || "",
    referrer: document.referrer || "",
    landingPage: window.location.pathname + window.location.search,
    firstVisitAt: new Date().toISOString(),
    sessionId: getSessionId(),
  };
  ctx.channel = detectChannel(ctx);
  return ctx;
}

/** Capture tracking on first visit / refresh URLs.
 *  - first-touch is never overwritten once set
 *  - last-touch is overwritten only when the URL brings new UTM data
 */
export function initTracking() {
  if (typeof window === "undefined") return;
  const incoming = readIncoming();
  if (!incoming) return;

  const first = readJSON(FIRST_KEY);
  if (!first) writeJSON(FIRST_KEY, incoming);

  const hasUtm =
    incoming.source ||
    incoming.medium ||
    incoming.campaign ||
    incoming.gclid ||
    incoming.fbclid ||
    incoming.ttclid ||
    incoming.ref;
  if (hasUtm) writeJSON(LAST_KEY, incoming);
}

/** Return the marketing context that should be attached to events.
 *  Prefers last-touch when it has UTM data, otherwise falls back to
 *  first-touch, otherwise a Direct context derived from the current URL.
 */
export function getTrackingContext() {
  const last = readJSON(LAST_KEY);
  const first = readJSON(FIRST_KEY);
  const fromStore = last || first || readIncoming() || {};
  // Always recompute channel from stored UTMs in case rules evolve.
  return { ...fromStore, channel: detectChannel(fromStore) };
}

/** Push to GTM dataLayer with no-op fallback. */
function pushDataLayer(event) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  try {
    window.dataLayer.push(event);
  } catch {
    /* ignore */
  }
}

/**
 * Record a WhatsApp click — fire-and-forget. Does NOT block the redirect:
 * the caller is expected to open WhatsApp immediately after invoking this.
 */
export function trackWhatsAppClick({
  vehicleId = "",
  vehicleName = "",
  language = "en",
  pickupDate = "",
  returnDate = "",
  destinationWhatsapp = "",
  destinationWaMe = "",
} = {}) {
  const ctx = getTrackingContext();
  const collaborator = detectCollaborator(ctx);
  const event = {
    eventType: "whatsapp_click",
    vehicleId,
    vehicleName,
    language,
    pickupDate,
    returnDate,
    destinationWhatsapp,
    destinationWaMe,
    source: ctx.source || "",
    medium: ctx.medium || "",
    channel: ctx.channel || "Direct",
    campaign: ctx.campaign || "",
    content: ctx.content || "",
    term: ctx.term || "",
    ref: ctx.ref || "",
    collaborator,
    gclid: ctx.gclid || "",
    fbclid: ctx.fbclid || "",
    ttclid: ctx.ttclid || "",
    landingPage: ctx.landingPage || "",
    currentPage: typeof window !== "undefined" ? window.location.pathname : "",
    referrer: ctx.referrer || "",
    sessionId: ctx.sessionId || getSessionId(),
    deviceType: deviceType(),
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
  };

  pushDataLayer({
    event: "whatsapp_click",
    vehicleName,
    vehicleId,
    language,
    destinationWhatsapp: destinationWaMe,
    source: event.source,
    medium: event.medium,
    channel: event.channel,
    campaign: event.campaign,
    ref: event.ref,
    collaborator,
    pagePath: typeof window !== "undefined" ? window.location.pathname : "",
  });

  api.post("/tracking/whatsapp-click", event).catch(() => {
    /* fire-and-forget */
  });

  return event;
}

/** Human-readable triplet (Origen / Campaña / Referencia) for the WhatsApp
 *  message body. Mirrors the rules in Prompt 8 §10. */
export function buildOriginBlock(ctx) {
  const c = ctx || getTrackingContext();
  const channel = c.channel || "Direct";
  let campaign = c.campaign || "";
  let ref = c.ref || "";

  if (channel === "Direct" || channel === "Website Normal") {
    campaign = campaign || "direct";
    ref = ref || "none";
  } else if (channel === "Google Ads") {
    campaign = campaign || "google_ads";
    ref = ref || "google_ads";
  } else if (channel === "Google Organic") {
    campaign = campaign || "google_organic";
    ref = ref || "google_organic";
  } else if (channel === "Instagram") {
    campaign = campaign || "instagram_bio";
    ref = ref || "instagram";
  } else if (channel === "Facebook") {
    campaign = campaign || "facebook_page";
    ref = ref || "facebook";
  } else if (channel === "TikTok") {
    campaign = campaign || "tiktok_bio";
    ref = ref || "tiktok";
  } else if (channel === "YouTube") {
    campaign = campaign || "youtube_description";
    ref = ref || "youtube";
  } else if (channel === "Collaborator") {
    campaign = campaign || "collaborator";
    ref = ref || "collaborator";
  } else {
    campaign = campaign || "unknown";
    ref = ref || "unknown";
  }
  return { channel, campaign, ref };
}
